import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { OrganizationTo } from '../../shared/models/organizationTo';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HomeService } from '../../home/home.service';
import { SharedService } from '../../shared/shared.service';
import { Alerts } from '../../shared/enums/alerts';
import { OrganizationsService } from '../organizations.service';
import { Roles } from '../../shared/enums/roles';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { OrgDefaultUserPreference } from 'src/app/shared/models/orgDefaultUserPreference';
import Swal from 'sweetalert2/dist/sweetalert2.js';

@Component({
    selector: 'app-org-default-user-settings',
    templateUrl: './org-default-user-settings.component.html',
    styleUrls: ['./org-default-user-settings.component.scss']
})
export class OrgDefaultUserSettingsComponent implements OnInit {
    selectedOrg: OrganizationTo;
    resetData: OrgDefaultUserPreference[] = [];
    loading: boolean;
    userRoles = Roles;
    isDisabled: boolean = false;
    isChanged: boolean = false;
    header = 'Default User Settings';
    title;
    options: UntypedFormGroup;
    preferenceFC: UntypedFormControl[] = [];
    preferences: OrgDefaultUserPreference[] = [];
    preferenceGroups: { label: string, items: { pref: OrgDefaultUserPreference, index: number }[] }[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) public data: OrganizationTo,
                public sharedService: SharedService,
                public dialogRef: MatDialogRef<OrgDefaultUserSettingsComponent>,
                public orgService: OrganizationsService,
                public homeService: HomeService,
                private fb: UntypedFormBuilder,
                private cdref: ChangeDetectorRef) {
        this.options = this.fb.group({
            hideRequired: false,
            floatLabel: 'auto'
        });
        this.title = data?.name;
    }

    ngOnInit() {
        this.loading = true;
        this.isDisabled = !(this.homeService.userDetails.roles.includes(this.userRoles.SUPER)
            || this.homeService.userDetails.roles.includes(this.userRoles.ADMIN));
        this.selectedOrg = this.data;
        this.loadPreferences();
    }

    ngAfterContentChecked() {
        this.cdref.detectChanges();
    }

    buildPreference(definition: any, storedValue: any): OrgDefaultUserPreference {
        return new OrgDefaultUserPreference({
            name: definition.name,
            displayName: definition.displayName,
            description: definition.description,
            regex: definition.regex,
            datatype: definition.datatype,
            value: storedValue,
            entitlementName: definition.entitlementName,
            entitlementDisplayName: definition.entitlementDisplayName,
            hasOverride: definition.hasOverride
        });
    }

    /**
     * Rebuilds preferenceGroups from the flat preferences/preferenceFC arrays, grouped by
     * owning entitlement for display (e.g. "Custom Map" settings and "EDB" settings render as
     * separate blocks). Indices into the flat arrays are preserved per item so the template
     * can still bind preferenceFC[index] directly.
     *
     * This is a plain method invoked explicitly whenever preferences changes (loadPreferences,
     * reset), NOT a template-bound getter: a getter re-runs on every change-detection cycle,
     * handing *ngFor a brand-new array/object reference each time, which makes it destroy and
     * recreate every child view on every tick. Combined with ngAfterContentChecked's
     * detectChanges() call below, that repeatedly tore down the ngbTooltip elements mid-open,
     * throwing NG0911 ("View has already been destroyed") once the tooltip's openDelay timer
     * fired against an already-destroyed view.
     */
    rebuildPreferenceGroups(): void {
        const groups = new Map<string, { pref: OrgDefaultUserPreference, index: number }[]>();
        this.preferences.forEach((pref, index) => {
            const label = pref.entitlementDisplayName || pref.entitlementName || 'Other';
            if (!groups.has(label)) {
                groups.set(label, []);
            }
            groups.get(label).push({ pref, index });
        });
        this.preferenceGroups = Array.from(groups.entries())
            .sort((a, b) => a[0] < b[0] ? -1 : 1)
            .map(([label, items]) => ({ label, items }));
    }

    /**
     * The backend returns one catalog entry per (entitlement, preference) pair, so a
     * preference entitled via more than one of the org's entitlements arrives more than once
     * with the same name. Rendering it as two separately-editable rows is actively dangerous:
     * updateSettings() builds the save payload keyed by name, so editing both rows to different
     * values would silently drop one edit with no warning. Keep exactly one row per name (the
     * first occurrence) -- it displays under one group, not duplicated.
     */
    dedupeByName(definitions: any[]): any[] {
        const seen = new Set<string>();
        return definitions.filter(def => {
            if (seen.has(def.name)) {
                return false;
            }
            seen.add(def.name);
            return true;
        });
    }

    loadPreferences() {
        // Single call: the backend returns the full catalog of preferences entitled to this
        // org (name/displayName/description/regex/datatype), each already carrying its stored
        // value or "false" if unset. No separate access-gate probe needed — icon visibility
        // (SUPER/ADMIN only) is the access control, per the ticket's own design constraint.
        this.orgService.getOrgDefaultUserPreferences(this.selectedOrg.clientId).subscribe({
            next: catalog => {
                const definitions: any[] = this.dedupeByName(JSON.parse(JSON.stringify(catalog || [])));
                this.resetData = definitions
                    // Only pass the stored value through when hasOverride is true -- otherwise
                    // the value is just the backend's synthesized "false" placeholder, and we
                    // want the model to derive a sensible regex-based default instead.
                    .map(def => this.buildPreference(def, def.hasOverride ? def.value : undefined))
                    .sort((a, b) => a.name < b.name ? -1 : 1);
                this.preferences = this.resetData.map(p => new OrgDefaultUserPreference(p));
                this.preferenceFC = this.preferences.map(p => this.getFormControl(p));
                this.rebuildPreferenceGroups();
                this.loading = false;
            },
            error: error => {
                this.dialogRef.close(false);
                this.sharedService.handleErrors(error, null, 'Unable to retrieve default user preferences for organization');
            }
        });
    }

    getFormControl(pref: OrgDefaultUserPreference) {
        // Values are always forced to lowercase (see OrgDefaultUserPreference), but a
        // preference's regex is often authored in uppercase (e.g. "^LIGHT|DARK$") -- validate
        // case-insensitively so a correct lowercase value never fails its own pattern.
        const control = pref.datatype === 'checkbox'
            ? new UntypedFormControl(pref.value, [Validators.required])
            : new UntypedFormControl(pref.value, [Validators.pattern(new RegExp(pref.regex || '.*', 'i'))]);
        // Not-yet-overridden preferences start disabled: updateSettings() only checks .valid
        // on enabled controls (see its comment), so a disabled row never blocks save on its
        // (possibly-blank) value -- this is what lets the user leave a preference untouched
        // without having to satisfy its regex.
        if (!pref.hasOverride || this.isDisabled) {
            control.disable();
        }
        return control;
    }

    /**
     * Toggles whether a preference is included as an org override. Enabling activates
     * validation/editing for that row; disabling exempts it from validation and drops it from
     * the save payload (updateSettings only sends preferences whose hasOverride is true), which
     * causes the backend's existing sync-delete behavior to remove any previously-saved value.
     */
    toggleOverride(index: number, checked: boolean) {
        this.preferences[index].hasOverride = checked;
        if (checked && !this.isDisabled) {
            this.preferenceFC[index].enable();
        } else {
            this.preferenceFC[index].disable();
        }
        this.change();
    }

    getErrorMessage(i: number) {
        return this.preferenceFC[i].hasError('required')
            ? 'You must enter a value'
            : 'Enter a valid value (' + this.preferences[i].regex + ')';
    }

    getDescription(pref: OrgDefaultUserPreference) {
        return pref.description + (pref.regex ? ' (Pattern: ' + pref.regex + ')' : '');
    }

    change() {
        this.isChanged = true;
    }

    updateSettings() {
        this.preferenceFC.forEach(fc => fc.markAsTouched());
        // A disabled control's status is DISABLED, not VALID -- fc.valid alone would treat
        // every not-overridden (disabled) preference as invalid and block save. Only enabled
        // (overridden) controls should be able to block save on an invalid value.
        if (this.preferenceFC.some(fc => fc.enabled && !fc.valid)) {
            return;
        }
        this.loading = true;
        // Backend now takes a list of {name, value} entries (matching the shape GET returns),
        // not a bare {name: value} map -- only name/value are read server-side.
        const payload = [];
        this.preferences.forEach((pref, i) => {
            if (pref.hasOverride) {
                const value = this.preferenceFC[i].value;
                // Force lowercase on save regardless of how the user typed it (validation is
                // case-insensitive, see getFormControl) -- stored values are lowercase by
                // convention (see OrgDefaultUserPreference). Checkbox values are booleans, not
                // strings, and are saved as-is.
                payload.push({
                    name: pref.name,
                    value: pref.datatype === 'checkbox' ? value : String(value).toLowerCase()
                });
            }
        });
        this.orgService.saveOrgDefaultUserPreferences(this.selectedOrg.clientId, payload).subscribe({
            next: () => {
                this.loading = false;
                this.sharedService.showAlert(Alerts.SUCCESS, 'Default User Settings updated successfully');
                this.dialogRef.close(true);
            },
            error: error => {
                this.loading = false;
                this.sharedService.handleErrors(error, null, error.error, true);
            }
        });
    }

    reset() {
        const doReset = () => {
            this.preferences = this.resetData.map(p => new OrgDefaultUserPreference(p));
            this.preferenceFC = this.preferences.map(p => this.getFormControl(p));
            this.rebuildPreferenceGroups();
            this.isChanged = false;
        };
        if (this.isChanged) {
            Swal.fire({
                title: 'Are you sure?',
                text: 'Your changes will be lost !',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Yes, Reset',
                cancelButtonText: 'No, Cancel'
            }).then(result => { if (result?.value) { doReset(); } });
        } else {
            doReset();
        }
    }

    close() {
        if (this.isChanged) {
            Swal.fire({
                title: 'Are you sure?',
                text: 'Your changes will not be saved !',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Yes, Close',
                cancelButtonText: 'No, Cancel'
            }).then(result => { if (result?.value) { this.dialogRef.close(false); } });
        } else {
            this.dialogRef.close(false);
        }
    }
}
